import "react-datepicker/dist/react-datepicker.css";
import { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import { ptBR } from "date-fns/locale";

import { cn } from "@/lib";

import { MessageError } from "../common";
import { X } from "lucide-react";

interface MonthYearPickerProps {
  date?: Date | null;
  onChange: (date: Date | null) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  isLoading?: boolean;
  isInvalid?: boolean | string | null;
  isValid?: boolean | string | null;
  messageError?: string;
  onClear?: () => void;
  placeholder?: string;
}

export function MonthYearPicker({
  date,
  onChange,
  onFocus,
  onBlur,
  className,
  isLoading,
  isInvalid,
  messageError,
  placeholder = "Selecione o mês e ano",
  onClear,
}: MonthYearPickerProps) {
  const [startDate, setStartDate] = useState<Date | null>(date || null);

  useEffect(() => {
    setStartDate(date || null);
  }, []);

  // converte date para UTC
  const convertToUTCDate = (localDate: Date): Date => {
    return new Date(
      Date.UTC(
        localDate.getFullYear(),
        localDate.getMonth() + 1,
        localDate.getDate()
      )
    );
  };

  const handleSelect = (date: Date | null) => {
    if (!date) {
      return;
    }

    const _date = convertToUTCDate(date);
    setStartDate(_date);
    onChange(_date);
  };

  const handleClear = () => {
    setStartDate(null);
    onChange(null);
    onClear?.();
  };

  return (
    <div className="space-y-1">
      <div className="relative">
        <DatePicker
          locale={ptBR}
          selected={startDate}
          onChange={handleSelect}
          onFocus={onFocus}
          onBlur={onBlur}
          dateFormat="MM/yyyy"
          showMonthYearPicker
          placeholderText={placeholder}
          className={cn(
            "flex h-10 w-full text-zinc-600 rounded-md border border-input bg-background px-4 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus:shadow-none focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            className,
            {
              "is-invalid": isInvalid,
            }
          )}
          disabled={isLoading}
        />
        {startDate && !isLoading && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
            aria-label="Limpar seleção"
          >
            <X size={16} />
          </button>
        )}
      </div>
      {messageError && <MessageError messageError={messageError} />}
    </div>
  );
}
